import { afterNextRender, DestroyRef, Directive, ElementRef, inject } from '@angular/core';

/**
 * Temporary workaround for a CCL defect (aw-component-lib 26.2.30, still present in 26.3.0).
 *
 * `aw-expansion-panel` hardcodes `role="button"`, `aria-label` and `aria-expanded` on its host element, which wraps
 * the projected content. Children of a `button` are presentational, so VoiceOver ignores every control inside the
 * panel (WCAG 4.1.2). The panel header already renders its own labelled expander button, so nothing is lost by
 * stripping them. The observer catches `aria-expanded`, which CCL re-sets on every toggle.
 *
 * With no header actions, CCL also makes the header row a focusable `role="button"` wrapped around that expander
 * button (nested interactive controls, axe `nested-interactive`). The row keeps its click handler; keyboard and
 * screen-reader users get the one real button.
 *
 * Delete this directive once CCL stops binding those attributes.
 */
@Directive({
  selector: 'aw-expansion-panel[appExpansionPanelA11y]',
})
export class ExpansionPanelA11yDirective {
  private static readonly HOST_ATTRIBUTES = ['role', 'aria-label', 'aria-expanded'];
  private static readonly HEADER_ATTRIBUTES = ['role', 'tabindex'];

  constructor() {
    const host: HTMLElement = inject(ElementRef).nativeElement;
    const removeAll = (element: Element | null, attributes: string[]) => {
      for (const attribute of attributes) {
        if (element?.hasAttribute(attribute)) element.removeAttribute(attribute);
      }
    };
    const strip = () => {
      removeAll(host, ExpansionPanelA11yDirective.HOST_ATTRIBUTES);
      removeAll(host.querySelector('.expansion-content'), ExpansionPanelA11yDirective.HEADER_ATTRIBUTES);
    };
    const observer = new MutationObserver(strip);

    afterNextRender(() => {
      strip();
      observer.observe(host, {
        attributes: true,
        subtree: true,
        attributeFilter: [...ExpansionPanelA11yDirective.HOST_ATTRIBUTES, 'tabindex'],
      });
    });
    inject(DestroyRef).onDestroy(() => observer.disconnect());
  }
}
